/* src/render/labels.ts — name sprites, shared by the flat far view and the globe.
 *
 * A pool of billboarded sprites over an LRU texture cache. Pools are small (a dozen or two
 * visible names), so labels cost their sprite count in draw calls — never one per place in
 * the dataset. Text is drawn once per unique string into a CanvasTexture and reused.
 */

import {
  CanvasTexture,
  Group,
  LinearFilter,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  type Object3D,
} from 'three';

interface CachedLabel {
  readonly texture: CanvasTexture;
  readonly aspect: number;
  lastUsed: number;
}

const CACHE_CAP = 96;
const cache = new Map<string, CachedLabel>();
let clock = 0;

function labelTexture(text: string): CachedLabel {
  clock += 1;
  const hit = cache.get(text);
  if (hit) {
    hit.lastUsed = clock;
    return hit;
  }

  const pad = 10;
  const fontPx = 34;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('labels: no 2d context');
  ctx.font = `700 ${String(fontPx)}px system-ui, sans-serif`;
  const w = Math.ceil(ctx.measureText(text).width) + pad * 2;
  const h = fontPx + pad * 2;
  canvas.width = w;
  canvas.height = h;
  const ctx2 = canvas.getContext('2d');
  if (!ctx2) throw new Error('labels: no 2d context');
  ctx2.font = `700 ${String(fontPx)}px system-ui, sans-serif`;
  ctx2.textAlign = 'center';
  ctx2.textBaseline = 'middle';
  // Halo first, fill second — readable over any terrain without a backing plate.
  ctx2.lineWidth = 7;
  ctx2.strokeStyle = 'rgb(10 14 10 / 82%)';
  ctx2.strokeText(text, w / 2, h / 2);
  ctx2.fillStyle = '#f2f5ef';
  ctx2.fillText(text, w / 2, h / 2);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearFilter;
  texture.generateMipmaps = false;

  const entry: CachedLabel = { texture, aspect: w / h, lastUsed: clock };
  cache.set(text, entry);

  if (cache.size > CACHE_CAP) {
    let oldest: string | null = null;
    let oldestUsed = Infinity;
    for (const [k, v] of cache) {
      if (v.lastUsed < oldestUsed) {
        oldestUsed = v.lastUsed;
        oldest = k;
      }
    }
    if (oldest !== null) {
      cache.get(oldest)?.texture.dispose();
      cache.delete(oldest);
    }
  }
  return entry;
}

export interface LabelItem {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** World height of the label text. */
  readonly size: number;
  readonly opacity?: number;
}

export class LabelPool {
  readonly root = new Group();
  readonly #sprites: Sprite[] = [];

  constructor(parent: Object3D, capacity: number) {
    for (let i = 0; i < capacity; i += 1) {
      const material = new SpriteMaterial({ depthTest: false, transparent: true });
      const sprite = new Sprite(material);
      sprite.visible = false;
      // Labels draw over the world, never through the HUD — late in the queue, no depth.
      sprite.renderOrder = 40;
      this.root.add(sprite);
      this.#sprites.push(sprite);
    }
    parent.add(this.root);
  }

  /** Show exactly these labels this frame; unused pool sprites hide. */
  show(items: readonly LabelItem[]): void {
    for (let i = 0; i < this.#sprites.length; i += 1) {
      const sprite = this.#sprites[i];
      if (!sprite) continue;
      const item = items[i];
      if (!item) {
        sprite.visible = false;
        continue;
      }
      const entry = labelTexture(item.text);
      const material = sprite.material;
      if (material.map !== entry.texture) {
        material.map = entry.texture;
        material.needsUpdate = true;
      }
      material.opacity = item.opacity ?? 1;
      sprite.position.set(item.x, item.y, item.z);
      sprite.scale.set(item.size * entry.aspect, item.size, 1);
      sprite.visible = true;
    }
  }

  hide(): void {
    for (const s of this.#sprites) s.visible = false;
  }

  dispose(): void {
    for (const s of this.#sprites) {
      s.material.dispose();
    }
    this.root.removeFromParent();
  }
}
